// deliveryRow.js
// -----------------------------------------------------------------------------
// The one place the deliveryList row and its deliveryTimeline document are
// written out, for the paths that use it: the up-country send (ส่งเข้าจัดรถ)
// and คิวรถเล็ก's confirm. managecar.vue still carries its own hand-written
// copies (eight of them) -- migrating those is follow-up work.
//
// `geo` and `landmark` have been dropped at a hop into deliveryList before, each
// time by someone writing the field list out again. This list is asserted by
// `npm run test:dispatch`; a new writer calls it rather than listing fields.
//
// A trip keeps the DEPARTURE day as `deliveryDate` on every row (as dispatch
// keys a 2-3 day trip in managecar) and carries `tripEnd`, the last day it may
// still deliver -- so stop ids, photo paths and reports stay what they were.
// Pure: no Firestore, no Vue.
// -----------------------------------------------------------------------------
const R = require('./bookingRules')

// Fields written only when they have a value, so a one-day job's row keeps
// exactly the shape it always had.
const OPTIONAL = ['tripEnd', 'planDate', 'planSeq', 'planDateWas', 'upTripId', 'sourceSlotId', 'transferAmt']

// The `createdAt` format คิวรถเล็ก has always written.
function stamp () {
  const d = new Date()
  return d.toISOString().slice(0, 10) + ' ' + d.toTimeString().slice(0, 8)
}

function utc (ymd) {
  const p = String(ymd).split('-').map(Number)
  return Date.UTC(p[0], p[1] - 1, p[2])
}
function addDays (ymd, n) { return new Date(utc(ymd) + n * 86400000).toISOString().slice(0, 10) }
function daysBetween (a, b) { return Math.round((utc(b) - utc(a)) / 86400000) }

const str = v => (v == null ? '' : String(v))

function listRow (job, sheet, at) {
  const j = job || {}
  const s = sheet || {}
  const row = {
    status: j.status || 'SO',
    transNo: str(s.transNo),
    loadDate: s.loadDate || '',
    regNum: s.regNum || '',
    driver: s.driver || '',
    driverName: s.driverName || '',
    deliveryDate: s.deliveryDate || '',
    cardCode: j.cardCode || '',
    cardPhone: j.cardPhone || '',
    cardName: j.cardName || '',
    custName: j.custName || '',
    custTel: j.custTel || '',
    time: j.time || null,
    custAddress: j.custAddress || '',
    geo: j.geo || '',
    landmark: j.landmark || '',
    prodList: j.prodList || [],
    prodListRt: j.prodListRt || [],
    soNo: str(j.soNo),
    rtNo: str(j.rtNo),
    rtDocEntry: j.rtDocEntry == null ? null : j.rtDocEntry,
    city: j.city || '',
    payMent: j.payMent || '',
    timeStampStart: '',
    timeStampStartBy: '',
    timeStampEnd: '',
    timeStampEndBy: '',
    timeUpdate: j.time || null,
    createdAt: at || stamp()
  }
  const extra = Object.assign({}, j, { tripEnd: s.tripEnd })
  OPTIONAL.forEach(k => { if (extra[k] != null && extra[k] !== '') row[k] = extra[k] })
  return row
}

function timelineDoc (job, sheet, at) {
  const j = job || {}
  const s = sheet || {}
  return {
    trackNo: str(j.status === 'RT' ? j.rtNo : j.soNo),
    date: s.deliveryDate || '',
    timeLine: [{ time: j.time || null, desc: 'กำลังส่งสินค้า', icon: 'mdi-truck-fast-outline' }],
    driverName: s.driverName || '',
    regNum: s.regNum || '',
    status: 'Y',
    // The timeline has always kept SAP's phone as typed, the row only its digits.
    custTel: j.timelineTel != null ? j.timelineTel : (j.custTel || ''),
    custName: j.custName || '',
    createdAt: at || stamp()
  }
}

// The row and its timeline, always together.
function pair (job, sheet) {
  const at = stamp()
  return { row: listRow(job, sheet, at), timeline: timelineDoc(job, sheet, at) }
}

// One order as SAP answered for it, in คิวรถเล็ก's field choices: SAP first and
// the booking second for พิกัด / จุดสังเกต (R.soPin drops an unreadable pin).
// `so` null means a return alone (an RT row); `rt` beside `so` is an exchange.
function jobFromSap ({ order, so, products, payment, rt, rtRows, time }) {
  const o = order || {}
  const src = so || rt || {}
  const pin = R.soPin(src)
  const job = {
    status: so ? 'SO' : 'RT',
    cardCode: src.cardCode || '',
    cardPhone: [src.phone1, src.phone2, src.phone3].filter(Boolean).join(' '),
    cardName: src.cardName || '',
    custName: src.custName || '',
    custTel: String(src.custTel || '').replace(/-/g, ''),
    timelineTel: src.custTel || '',
    custAddress: src.custAddress || '',
    geo: pin.geo || o.geo || '',
    landmark: pin.landmark || o.landmark || '',
    city: src.city || '',
    prodList: so ? (products || []) : [],
    prodListRt: rt ? (rtRows || []) : [],
    soNo: so ? str(o.soNo) : '',
    rtNo: rt ? str(rt.rtNo) : '',
    rtDocEntry: rt ? rt.docEntry : null,
    payMent: payment || '',
    time: time || null,
    sourceSlotId: o.id
  }
  if (!so && rt && rt.transferAmt) job.transferAmt = rt.transferAmt
  return job
}

// ใบขนส่งสินค้า's order for a planned trip: by planSeq, unplanned last, with a
// `dayHead` ({ day, date }) on the first stop of each planned day. null when
// nothing on the sheet carries a plan -- the sheet then keeps its own order.
function sheetDays (groups) {
  const list = (groups || []).slice()
  if (!list.some(g => g && g.planSeq != null)) return null
  const BIG = 1e9
  list.sort((a, b) => (a.planSeq == null ? BIG : a.planSeq) - (b.planSeq == null ? BIG : b.planSeq))
  const dates = []
  list.forEach(g => { if (g.planDate && dates.indexOf(g.planDate) === -1) dates.push(g.planDate) })
  dates.sort()
  let last = null
  return list.map(g => {
    const head = g.planDate && g.planDate !== last ? { day: dates.indexOf(g.planDate) + 1, date: g.planDate } : null
    if (g.planDate) last = g.planDate
    return Object.assign({}, g, { dayHead: head })
  })
}

// จัดรถ's sheet table (managecar.vue): each SO / RT line with its customer and
// the plan's day, read from the sheet's deliveryList rows (`rowFor(item)`).
// A sheet with a plan is listed in plan order -- a trip keeps its departure day
// as deliveryDate, so เวลาส่ง alone put a day-2 09:00 drop above a day-1 14:00
// one -- with lines added by hand after it, by time. Any other sheet stays in
// เวลาส่ง order, as it always was.
function sheetLines (items, rowFor) {
  const lines = (items || []).map(it => {
    const r = (rowFor && rowFor(it)) || {}
    return Object.assign({}, it, {
      custName: r.custName || '',
      custAddress: r.custAddress || '',
      planDate: r.planDate || '',
      planSeq: r.planSeq == null ? null : r.planSeq,
      time: it.time || r.time || ''
    })
  })
  const dates = []
  lines.forEach(l => { if (l.planDate && dates.indexOf(l.planDate) === -1) dates.push(l.planDate) })
  dates.sort()
  lines.forEach(l => { l.planDay = l.planDate ? dates.indexOf(l.planDate) + 1 : null })
  const BIG = 1e9
  const byTime = (a, b) => String(a.time || '').localeCompare(String(b.time || ''))
  const planned = lines.some(l => l.planSeq != null)
  lines.sort((a, b) => planned
    ? ((a.planSeq == null ? BIG : a.planSeq) - (b.planSeq == null ? BIG : b.planSeq)) || byTime(a, b)
    : byTime(a, b))
  return lines
}

// คิวรถเล็ก's รายละเอียดนัดหมาย for a planned 2-day route: plan order (planSeq,
// then time), each planned day opened by a `dayHead` ({ day, date }) counted
// from the departure date -- not from the first date on the list, so a route
// whose day-1 stops are all elsewhere still says วันที่ 2. Unplanned rows go
// last, by time, under no heading. null when no row carries a plan: the dialog
// keeps its own order by time.
function planOrder (rows, departureDate) {
  const list = (rows || []).slice()
  if (!list.some(r => r && r.planSeq != null)) return null
  const BIG = 1e9
  const startOf = r => {
    const m = String((r && r.time) || '').match(/^(\d{1,2}):(\d{2})/)
    return m ? Number(m[1]) * 60 + Number(m[2]) : BIG
  }
  list.sort((a, b) => ((a.planSeq == null ? BIG : a.planSeq) - (b.planSeq == null ? BIG : b.planSeq)) || (startOf(a) - startOf(b)))
  let last = null
  return list.map(r => {
    const head = r.planSeq != null && r.planDate && r.planDate !== last
      ? { day: departureDate ? daysBetween(departureDate, r.planDate) + 1 : null, date: r.planDate }
      : null
    if (r.planSeq != null && r.planDate) last = r.planDate
    return Object.assign({}, r, { dayHead: head })
  })
}

// The day fields แก้จำนวนวัน writes on one job or row of a route leaving on
// `departure`, now running `days` (1 | 2). 2 -> 1 moves a planned day-2 stop
// onto the departure day, as it must, but keeps the day it had in
// `planDateWas`; 1 -> 2 puts it back. Without that, flipping 2 -> 1 -> 2 left
// every stop on day 1 (2026-10-15, the ชลบุรี route's RT pickup at 08:00 of
// day 2 listed first on day 1). A row with no planDate gets none invented.
function daysChange (row, departure, days) {
  const r = row || {}
  if (Number(days) > 1) {
    const out = { tripEnd: addDays(departure, Number(days) - 1) }
    if (r.planDateWas) Object.assign(out, { planDate: r.planDateWas, planDateWas: null })
    return out
  }
  const out = { tripEnd: null }
  if (!r.planDate) return out
  out.planDate = departure
  if (r.planDate !== departure) out.planDateWas = r.planDate
  return out
}

// The deliveryTimeline document of one sheet row, among every timeline of its
// number. An SO delivered before (a rebooking after ส่งไม่สำเร็จ, a return
// collected twice) has one timeline per visit; the sheet's is the one with its
// date and plate. A lone timeline that matches neither is still taken, as the
// page always did; with several and no match the answer is null, and the
// caller refuses rather than deleting another visit's record.
function timelineFor (timelines, row) {
  const list = timelines || []
  const r = row || {}
  const hit = list.find(t => t.date === r.deliveryDate && t.regNum === r.regNum)
  if (hit) return hit
  return list.length === 1 ? list[0] : null
}

module.exports = { OPTIONAL, listRow, timelineDoc, pair, jobFromSap, addDays, daysBetween, sheetDays, sheetLines, planOrder, daysChange, timelineFor }
